"use client";

import { NextIntlClientProvider } from "next-intl";
import { ComponentProps } from "react";

import { reportSilentFailure } from "@/lib/reportSilentFailure";

import { formatIntlIssueMessage } from "./missingTranslation";

// Define error codes locally since they're not exported from next-intl v4
const IntlErrorCode = {
  MISSING_MESSAGE: "MISSING_MESSAGE",
  ENVIRONMENT_FALLBACK: "ENVIRONMENT_FALLBACK",
  INSUFFICIENT_PATH: "INSUFFICIENT_PATH",
  INVALID_MESSAGE: "INVALID_MESSAGE",
} as const;

// Flattened set of every existing LEAF message path (string messages only —
// NOT intermediate object nodes), built ONCE from the already-hydrated client
// messages (see <IntlPathRegistrar/> + registerMessagePaths below) — no extra
// bundle weight, since these messages are already shipped to the client by
// next-intl's hydration.
//
// This lets getMessageFallback recognise the repo's documented
// "<field>.<variant>" + "<field>.default" fallback convention (country-aware
// address fields, bankAccount.fields.*, etc. — see
// .claude/rules/web/domains/country-aware-translations.md) and stay SILENT for
// an INTENTIONAL variant probe. Only a genuinely orphaned key (no `default`
// LEAF anywhere on its path → a real, user-visible blank) is worth a Sentry event.
//
// NOTE: `messagePathsReady` is process-global and assumes a single message
// catalogue. The app is single-locale (en-GB); revisit if per-locale catalogues
// are ever introduced.
let messagePathsReady = false;
const messagePaths = new Set<string>();

const flattenInto = (node: unknown, prefix: string): void => {
  if (node !== null && typeof node === "object") {
    for (const [k, v] of Object.entries(node)) {
      flattenInto(v, prefix ? `${prefix}.${k}` : k);
    }
    return;
  }
  // Leaf only. Registering intermediate object paths too would let the sibling
  // check match an OBJECT node — e.g. a genuinely-missing
  // `fields.accountNumber.helpText` would be wrongly silenced by the existing
  // `fields.accountNumber.default` OBJECT; only a real leaf fallback
  // (`fields.accountNumber.default.placeholder`) should count.
  if (prefix) messagePaths.add(prefix);
};

/**
 * Populate the message-path set once from the hydrated client messages.
 * Idempotent — safe to call on every render of the registrar component.
 */
export const registerMessagePaths = (messages: unknown): void => {
  if (messagePathsReady) return;
  flattenInto(messages, "");
  messagePathsReady = true;
};

/**
 * A missing key is an EXPECTED variant probe when replacing any single path
 * segment with "default" yields a LEAF message that DOES exist — e.g.
 *   address.firstName.label.FR  →  address.firstName.label.default
 *   bankAccount.fields.accountNumber.HK.placeholder
 *                               →  bankAccount.fields.accountNumber.default.placeholder
 * The call site's `|| t("…default")` / `.raw()[country] ?? .default` fallback
 * renders that default, so there is no blank to alert on. The swap is
 * position-independent so it catches both trailing-variant (address) and
 * mid-path-variant (bankAccount) shapes.
 *
 * If the path set isn't ready yet (the very first render, or SSR where the
 * client registrar hasn't run), we conservatively return false → report. The
 * dedup set below keeps any such stray early event to a single occurrence.
 *
 * Exported for unit testing.
 */
export const hasDefaultVariantSibling = (path: string): boolean => {
  if (!messagePathsReady) return false;
  const segments = path.split(".");
  for (let i = 0; i < segments.length; i++) {
    if (segments[i] === "default") continue;
    const candidate = segments
      .map((segment, j) => (j === i ? "default" : segment))
      .join(".");
    if (messagePaths.has(candidate)) return true;
  }
  return false;
};

// t() runs on every render — without this dedup a missing key on a list row
// would emit hundreds of identical events per session.
const reportedIntlPaths = new Set<string>();

const reportIntlIssue = (code: string, path: string) => {
  const dedupKey = `${code}:${path}`;
  if (reportedIntlPaths.has(dedupKey)) return;
  reportedIntlPaths.add(dedupKey);
  // Translation keys are developer-authored identifiers — PII-safe. The E2E
  // fixture parses the MISSING_MESSAGE form of this message from the console.
  reportSilentFailure({
    message: formatIntlIssueMessage(code, path),
    level: "warning",
    fingerprint: `i18n-${code}-${path}`,
  });
};

type NextIntlClientProviderProps = ComponentProps<
  typeof NextIntlClientProvider
>;

export const getMessageFallback: NextIntlClientProviderProps["getMessageFallback"] =
  ({
    namespace,
    key,
    error,
  }: {
    namespace?: string;
    key: string;
    error: { code: string };
  }) => {
    const path = [namespace, key].filter((part) => part != null).join(".");
    if (error.code === IntlErrorCode.MISSING_MESSAGE) {
      // Renders as an empty string. Report it ONLY when there is no documented
      // `.default` fallback on the path — i.e. an actual user-visible blank.
      // Intentional country/variant probes (which DO have a `.default` sibling
      // the call site falls back to) are silenced: they were the bulk of the
      // [i18n] MISSING_MESSAGE noise (e.g. address.postTown.placeholder.undefined,
      // bankAccount.fields.accountNumber.HK.placeholder — June 2026).
      if (!hasDefaultVariantSibling(path)) {
        reportIntlIssue(error.code, path);
      }
      return "";
    }
    return path;
  };

export const onError: NextIntlClientProviderProps["onError"] = (error: {
  code: string;
  message?: string;
}) => {
  // MISSING_MESSAGE is reported (with the exact key) in getMessageFallback.
  if (error.code === IntlErrorCode.MISSING_MESSAGE) return;
  if (error.code === IntlErrorCode.ENVIRONMENT_FALLBACK) return;
  console.error(error);
  // INVALID_MESSAGE / INSUFFICIENT_PATH: broken ICU syntax or wrong
  // namespace depth — user sees the raw key path instead of copy. The
  // message text is developer-authored (key + ICU parse detail), PII-safe.
  reportIntlIssue(error.code, error.message?.slice(0, 120) ?? "unknown");
};
